import React, { useState } from 'react';
import { useFetch } from '../hooks/useFetch';
import { platformAPI, PLATFORMS } from '../api/client';
import { Card, Badge, Modal, Alert, Btn, FormInput, FormSelect, Spinner, EmptyState, fmtDate } from '../components/UI';

const PLATFORM_OPTS = PLATFORMS.map(p => ({ value: p, label: p }));

const STATUS_INFO = {
  submitted:    { label:'Submitted',    color:'#f4a61d', bg:'#fef9c3' },
  under_review: { label:'Under Review', color:'#0891b2', bg:'#e0f2fe' },
  active:       { label:'Active',       color:'#16a34a', bg:'#dcfce7' },
  paused:       { label:'Paused',       color:'#636b89', bg:'#f0f2f8' },
  rejected:     { label:'Rejected',     color:'#dc2626', bg:'#fee2e2' },
};

function platformColor(name) {
  const map = { 'Appen':'#ff6b35','Outlier AI':'#0891b2','oneForma':'#4f6ef7','Remotasks':'#16a34a','Toloka':'#f4a61d','Prolific':'#7c3aed','DataAnnotation':'#dc2626','Clickworker':'#0f766e','iSoftStone':'#1d4ed8','Lionbridge':'#9333ea','Hive Microtasks':'#b45309','TELUS AI':'#15803d' };
  return map[name] || '#9099b4';
}

export default function PlatformAccounts() {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState({ platform_name: '', username: '', password: '', notes: '' });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const { data, loading, refetch } = useFetch(() => platformAPI.list());
  const accounts = data?.accounts || [];

  const handle = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.platform_name || !form.username || !form.password) {
      setError('Platform, username and password are all required.');
      return;
    }
    setSaving(true); setError('');
    try {
      await platformAPI.submit(form);
      setSuccess('Platform account submitted successfully! Our team will review it shortly.');
      setShowModal(false);
      setForm({ platform_name:'', username:'', password:'', notes:'' });
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit account.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding:'24px 20px', fontFamily:"'DM Sans',sans-serif", maxWidth:1000, margin:'0 auto' }}>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:14 }}>
        <div>
          <h1 style={{ fontFamily:"'Sora',sans-serif", fontSize:22, fontWeight:700, color:'#1a1f5e', margin:0 }}>Platform Accounts</h1>
          <p style={{ fontSize:14, color:'#9099b4', marginTop:4 }}>Submit your AI platform credentials for management</p>
        </div>
        <Btn onClick={() => { setShowModal(true); setError(''); }}>+ Submit Account</Btn>
      </div>

      {success && (
        <div style={{ background:'#dcfce7', border:'1px solid #86efac', borderRadius:12, padding:'12px 16px', marginBottom:20, fontSize:14, color:'#16a34a', fontWeight:500 }}>
          ✅ {success}
        </div>
      )}

      {/* ── IMPORTANT NOTICE ── */}
      <div style={{ background:'linear-gradient(135deg,#eef1ff,#f0f4ff)', border:'1px solid #c7d2fe', borderRadius:14, padding:'20px 22px', marginBottom:24 }}>
        <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
          <div style={{ width:42, height:42, borderRadius:10, background:'#4f6ef7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>💬</div>
          <div>
            <h3 style={{ fontFamily:"'Sora',sans-serif", fontSize:15, fontWeight:700, color:'#1a1f5e', margin:'0 0 6px' }}>
              Haven't created your platform account yet?
            </h3>
            <p style={{ fontSize:14, color:'#636b89', lineHeight:1.65, margin:'0 0 12px' }}>
              Before submitting, make sure you've spoken with our support team. We'll advise you on <strong>which platform pays best in your region</strong>, help you create your account, and guide you through any onboarding tests — completely free.
            </p>
            <a href="mailto:hello@quvera.io?subject=Platform Account Setup Help"
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 18px', background:'#4f6ef7', color:'#fff', borderRadius:10, fontSize:13, fontWeight:700, textDecoration:'none' }}>
              📧 Contact Support First — hello@quvera.io
            </a>
          </div>
        </div>
      </div>

      {/* Accounts list */}
      {loading ? <Spinner /> : accounts.length === 0 ? (
        <Card>
          <EmptyState
            icon="🔗"
            message="No platform accounts submitted yet."
            sub="Contact our support team first to get guidance on which platform suits you, then submit your account here."
          />
        </Card>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
          {accounts.map(acc => {
            const statusInfo = STATUS_INFO[acc.status] || STATUS_INFO.submitted;
            const color = platformColor(acc.platform_name);
            return (
              <div key={acc.id} style={{ background:'#fff', borderRadius:16, border:'1px solid #e2e6f0', padding:20, boxShadow:'0 2px 8px rgba(15,18,51,.05)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:color, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, fontWeight:800, flexShrink:0 }}>
                    {acc.platform_name.slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Sora',sans-serif", fontSize:15, fontWeight:700, color:'#1a1f5e' }}>{acc.platform_name}</div>
                    <div style={{ fontSize:12, color:'#9099b4' }}>Submitted {fmtDate(acc.created_at)}</div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:statusInfo.bg, color:statusInfo.color, whiteSpace:'nowrap' }}>
                    {statusInfo.label}
                  </span>
                </div>
                <div style={{ fontSize:13, color:'#636b89' }}>
                  <span style={{ fontWeight:600, color:'#1a1f5e' }}>Username: </span>{acc.username}
                </div>
                {acc.notes && (
                  <div style={{ fontSize:13, color:'#9099b4', marginTop:6, fontStyle:'italic' }}>{acc.notes}</div>
                )}
                {acc.status === 'active' && (
                  <div style={{ marginTop:12, padding:'10px 14px', background:'#f0fdf4', borderRadius:10, fontSize:13, color:'#16a34a', fontWeight:600 }}>
                    ✅ Active — earnings are being generated
                  </div>
                )}
                {acc.status === 'rejected' && (
                  <div style={{ marginTop:12, padding:'10px 14px', background:'#fee2e2', borderRadius:10, fontSize:13, color:'#dc2626' }}>
                    ❌ Rejected — contact support for help
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Submit Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Submit Platform Account">
        {/* Reminder inside modal */}
        <div style={{ background:'#fef9c3', border:'1px solid #fcd34d', borderRadius:10, padding:'12px 14px', marginBottom:18, fontSize:13, color:'#92400e', lineHeight:1.6 }}>
          ⚠️ <strong>Before submitting:</strong> Have you already spoken to our support team at{' '}
          <a href="mailto:hello@quvera.io" style={{ color:'#b45309', fontWeight:700 }}>hello@quvera.io</a>?
          {' '}We recommend contacting us first so we can guide you to the best platform for your region.
        </div>
        <Alert type="error" message={error} />
        <form onSubmit={submit}>
          <FormSelect label="Platform" options={PLATFORM_OPTS} value={form.platform_name} onChange={handle('platform_name')} />
          <FormInput label="Username / Email" placeholder="Your platform login email or username" value={form.username} onChange={handle('username')} required />
          <FormInput label="Password" type="password" placeholder="Your platform account password" value={form.password} onChange={handle('password')} required />
          <FormInput label="Notes (optional)" placeholder="Anything we should know about this account" value={form.notes} onChange={handle('notes')} />
          <p style={{ fontSize:12, color:'#9099b4', marginBottom:16, lineHeight:1.6 }}>
            🔒 Your credentials are encrypted with AES-256 military-grade security. Only your assigned professional can access them.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <Btn variant="outline" type="button" onClick={() => setShowModal(false)}>Cancel</Btn>
            <Btn loading={saving} type="submit">Submit Account</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
